import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { FaCalculator } from 'react-icons/fa';
import 'react-datepicker/dist/react-datepicker.css';
import './Quoter.css';
import { toZonedTime } from 'date-fns-tz';
import { parsePhoneNumber } from 'libphonenumber-js/min';
import { isPhonePossible, toPhoneE164, normalizePhone } from '../utils/phone';
import { getDialEntry } from '../utils/dialCodes';
import { esProductoAnual, finDeMultiplesViajes, mismoDia } from '../utils/multiplesViajes';

// New components
import SearchBar from './SearchBar/SearchBar';
import MobileOverlay from './MobileFlow/MobileOverlay';
import InsuranceTypeField from './fields/InsuranceTypeField';
import OriginField from './fields/OriginField';
import DestinationField from './fields/DestinationField';
import ProductField from './fields/ProductField';
import DateField from './fields/DateField';
import PassengersField from './fields/PassengersField';
import { useResponsiveLayout } from './hooks/useResponsiveLayout';
import { leerMarcaClic } from '../utils/marcaClic';

// Timezone mapping
const COUNTRY_TIMEZONES = {
  'Argentina': 'America/Argentina/Buenos_Aires',
  'Bolivia': 'America/La_Paz',
  'Brasil': 'America/Sao_Paulo',
  'Canadá': 'America/Toronto',
  'Chile': 'America/Santiago',
  'Colombia': 'America/Bogota',
  'Costa Rica': 'America/Costa_Rica',
  'Ecuador': 'America/Guayaquil',
  'El Salvador': 'America/El_Salvador',
  'España': 'Europe/Madrid',
  'Guatemala': 'America/Guatemala',
  'Honduras': 'America/Tegucigalpa',
  'México': 'America/Mexico_City',
  'Panamá': 'America/Panama',
  'Paraguay': 'America/Asuncion',
  'Perú': 'America/Lima',
  'República Dominicana': 'America/Santo_Domingo',
  'Uruguay': 'America/Montevideo',
  'Venezuela': 'America/Caracas',
  'Mundial': 'UTC'
};

function getQuoterIdSafe() {
  if (typeof window === 'undefined') return null;
  const fromGlobal = window?.QuoterConfig?.quoterId;
  if (fromGlobal) return String(fromGlobal);
  try {
    const scripts = Array.from(document.getElementsByTagName('script'));
    for (const s of scripts) {
      const src = s.getAttribute('src');
      if (!src) continue;
      if (src.includes('cotizador.js')) {
        const u = new URL(src, window.location.origin);
        const qp = u.searchParams.get('quoterId');
        if (qp) return qp;
      }
    }
  } catch (e) {}
  try {
    const el = document.getElementById('cotizador-viajes');
    const d = el?.dataset?.quoterId;
    if (d) return d;
  } catch (e) {}
  try {
    const u = new URL(window.location.href);
    const qp = u.searchParams.get('quoterId');
    if (qp) return qp;
  } catch (e) {}
  return null;
}

function getClientKeySafe() {
  if (typeof window === 'undefined') return null;
  const fromGlobal = window?.QuoterConfig?.clientKey;
  if (fromGlobal) return String(fromGlobal);
  try {
    const scripts = Array.from(document.getElementsByTagName('script'));
    for (const s of scripts) {
      const src = s.getAttribute('src');
      if (!src) continue;
      if (src.includes('cotizador.js')) {
        // ⚠️ La base NO puede ser window.location.origin.
        //
        // Dentro de un iframe srcdoc el documento es about:srcdoc y ese origin
        // vale la CADENA "null". new URL(src, "null") lanza «Invalid base URL»
        // —también con src absoluto, porque el constructor valida la base antes
        // de mirar el src—, el catch de abajo se lo tragaba y esta función
        // devolvía null con la clave delante. Eso mandó el POST sin cabecera
        // x-client-key desde el portal de agencias, y como ese dominio está en
        // la lista de TODAS las agencias, la cotización se atribuyó a otra.
        //
        // Un src absoluto no necesita base; solo uno relativo, y para ese sirve
        // href (que en srcdoc es "about:srcdoc", igual de inútil, pero ahí el
        // src siempre es absoluto porque lo escribe el anfitrión).
        const base = /^[a-z][a-z0-9+.-]*:/i.test(src) ? undefined : window.location.href;
        const u = new URL(src, base);
        const key = u.searchParams.get('key');
        if (key) return key;
      }
    }
  } catch (e) {}
  return null;
}

const Quoter = ({ className }) => {
  // ============ RESPONSIVE ============
  const layoutMode = useResponsiveLayout();

  // ============ STATE ============
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  // URL de planes cuando el navegador bloqueó la ventana emergente: se ofrece
  // como enlace para que un segundo clic del usuario la abra.
  const [urlPlanesBloqueada, setUrlPlanesBloqueada] = useState(null);
  const [selectedOrigin, setSelectedOrigin] = useState(null);
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [detectedCountryCode, setDetectedCountryCode] = useState('CO');
  const [dialCountryCode, setDialCountryCode] = useState('CO');
  const [product, setProduct] = useState('');
  const [passengerCounts, setPassengerCounts] = useState({});
  const [minDateForPicker, setMinDateForPicker] = useState(new Date());
  const [minDaysAdjusted, setMinDaysAdjusted] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [quoterId, setQuoterId] = useState(null);
  const [clientKey, setClientKey] = useState(null);
  const [quoterDataFromDetails, setQuoterDataFromDetails] = useState(null);
  const [geolocating, setGeolocating] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    destino: false, fechas: false, viajeros: false, producto: false, email: false, phone: false
  });
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Airbnb UI state
  const [activePanel, setActivePanel] = useState(null);
  const [isMobileFlowOpen, setIsMobileFlowOpen] = useState(false);
  const [mobileActiveStep, setMobileActiveStep] = useState(null);
  const [originConfirmed, setOriginConfirmed] = useState(false);


  // ============ REFS ============
  const destinationDebounceTimer = useRef(null);
  const passengersDebounceTimer = useRef(null);
  const isHydrating = useRef(false);
  const userClearedOrigin = useRef(false);
  const dialCodeManuallySet = useRef(false);
  const selectedInsuranceRef = useRef(null);
  const productHydrated = useRef(false);

  // ============ COMPUTED ============
  const guests = Object.values(passengerCounts).reduce((sum, count) => sum + count, 0);

  const effectiveQuoterId = useMemo(() => {
    const fromDetails = quoterDataFromDetails?.id;
    const fromState = quoterId;
    const idStr = (fromDetails != null ? String(fromDetails) : (fromState != null ? String(fromState) : '0'));
    return idStr || '0';
  }, [quoterDataFromDetails?.id, quoterId]);

  // ============ HELPERS ============
  const removeAccents = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const customFilterOption = (option, inputValue) => {
    if (!inputValue) return false; // Solo mostrar resultados cuando hay búsqueda
    const searchValue = removeAccents(inputValue.toLowerCase().trim());
    let countryName = '';
    if (option.data) {
      if (option.data.data && option.data.data.name) {
        countryName = option.data.data.name;
      } else if (option.data.name) {
        countryName = option.data.name;
      } else return false;
    } else return false;
    const normalizedCountryName = removeAccents(countryName.toLowerCase());
    return normalizedCountryName.includes(searchValue);
  };

  // ============ EFFECTS - QuoterId & Details ============
  useEffect(() => {
    const id = getQuoterIdSafe();
    setQuoterId(id);
    setClientKey(getClientKeySafe());
  }, []);

  useEffect(() => {
    if (!quoterId) return;
    const fetchDetailsAndLog = async () => {
      try {
        const url = `${process.env.REACT_APP_API_BACKEND}/external/landing/details/${quoterId}`;
        const resp = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
        if (!resp.ok) return;
        const result = await resp.json();
        const q = (result?.data?.quoter) || result?.data || result?.quoter || null;
        if (!q) return;
        const mapped = {
          id: q.id ?? null, productId: q.productId ?? null, subproductId: q.subproductId ?? null,
          quoterValor: q.quoterValor ?? null, countryOriginName: q.countryOriginName ?? null,
          countryDestinationName: q.countryDestinationName ?? null, quoterNum: q.quoterNum ?? null,
          ages: q.ages ?? null, initialDate: (q.initialDate ?? q.initial_date) ?? null,
          endDate: (q.endDate ?? q.end_date) ?? null, productName: q.productName ?? null,
          productSubName: q.productSubName ?? null, countryOriginId: q.countryOriginId ?? null,
          countryDestinationId: q.country_destinations ?? null, email: q.email ?? null, phone: q.phone ?? null,
        };
        setQuoterDataFromDetails(mapped);
        setSelectedOrigin(q.countryOriginId);
        if (!quoterId && q.id) setQuoterId(String(q.id));
        if (q.email) setEmail(q.email);
        if (q.phone) {
          setPhone(q.phone);
          // Si el teléfono guardado ya viene con +indicativo, pre-separamos el
          // indicativo para que el selector lo muestre correctamente y no lo
          // pise la sincronización por origen/geolocalización.
          try {
            // Normalizado primero: un teléfono guardado con el "1" mexicano
            // no se parsea y el selector se quedaba sin detectar el país.
            const cleaned = normalizePhone(q.phone, null);
            if (cleaned.startsWith('+')) {
              const parsed = parsePhoneNumber(cleaned);
              if (parsed?.country) {
                setDialCountryCode(parsed.country);
                dialCodeManuallySet.current = true;
              }
            }
          } catch (e) {}
        }
      } catch (e) {}
    };
    fetchDetailsAndLog();
  }, [quoterId]);

  // ============ EFFECTS - Insurance Reset ============
  useEffect(() => {
    if (selectedInsurance) {
      setSelectedOrigin(null);
      setSelectedDestinations([]);
      setProduct("");
      const initialCounts = {};
      if (selectedInsurance.passengers?.length > 0) {
        selectedInsurance.passengers.forEach(p => { initialCounts[p.id] = 0; });
      }
      setPassengerCounts(initialCounts);
    }
  }, [selectedInsurance]);

  // ============ EFFECTS - Preselection from Details ============
  useEffect(() => {
    const q = quoterDataFromDetails;
    if (!q) return;
    isHydrating.current = true;
    const parseDate = (d) => {
      if (!d) return null;
      if (d instanceof Date) return d;
      if (typeof d === 'string') {
        const iso = /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00` : d;
        const dt = new Date(iso);
        return isNaN(dt.getTime()) ? null : dt;
      }
      return null;
    };
    let s = parseDate(q.initialDate);
    let e = parseDate(q.endDate);
    // Cotización vieja reabierta con «Editar» (Comercial, 11/10/2026): si la
    // salida guardada ya pasó, NO se recargan esas fechas. El calendario no
    // deja elegir días pasados, pero una fecha precargada se colaba igual y se
    // volvía a guardar. Se dejan vacías y marcadas para que elija de nuevo.
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    if (s && s < hoy) {
      s = null;
      e = null;
      setStartDate(null);
      setValidationErrors(prev => ({ ...prev, fechas: true }));
    }
    if (s) setStartDate(s);
    const dateType = selectedInsurance?.dateType;
    if (dateType === 'range') {
      setEndDate(e || null);
    } else if (dateType === 'date') {
      // Multiviajes/anual: la llegada SIEMPRE se deriva de la salida, incluso
      // si la cotización guardada trae otra. Antes se prefería la guardada, y
      // eso hacía que una fila ya fugada —las hay en producción— se recargara
      // con su rango malo y se volviera a guardar igual al editar.
      setEndDate(finDeMultiplesViajes(s));
    } else {
      setEndDate(null);
    }
  }, [quoterDataFromDetails, selectedInsurance]);

  useEffect(() => {
    const desiredId = quoterDataFromDetails?.countryOriginId;
    if (!desiredId || !selectedInsurance?.origins?.length) return;
    const match = selectedInsurance.origins.find(o => o?.country?.id && String(o.country.id) === String(desiredId));
    if (match?.country?.id != null) setSelectedOrigin(match.country.id);
  }, [quoterDataFromDetails, selectedInsurance]);

  useEffect(() => {
    const desired = quoterDataFromDetails?.countryDestinationId;
    if (desired == null || !selectedInsurance?.destinations?.length) return;
    const arr = Array.isArray(desired) ? desired : [desired];
    const desiredSet = new Set(arr.map(id => String(id)));
    const validIds = selectedInsurance.destinations.map(d => d?.country?.id).filter(id => id != null && desiredSet.has(String(id)));
    setSelectedDestinations(validIds);
  }, [quoterDataFromDetails, selectedInsurance]);

  useEffect(() => {
    const subId = quoterDataFromDetails?.subproductId;
    if (subId == null || !selectedInsurance?.subs?.length) return;
    const match = selectedInsurance.subs.find(s => String(s?.id) === String(subId));
    if (match?.id != null) setProduct(match.id);
  }, [quoterDataFromDetails, selectedInsurance]);

  useEffect(() => {
    const agesArr = quoterDataFromDetails?.ages;
    if (!Array.isArray(agesArr) || agesArr.length === 0 || !selectedInsurance?.passengers?.length) return;
    const nextCounts = {};
    agesArr.forEach(a => {
      if (!a) return;
      const target = selectedInsurance.passengers.find(p =>
        (p?.age_id != null && String(p.age_id) === String(a.id)) || String(p?.id) === String(a.id)
      );
      if (target && typeof a.count === 'number' && a.count >= 0) nextCounts[target.id] = a.count;
    });
    if (Object.keys(nextCounts).length > 0) setPassengerCounts(prev => ({ ...prev, ...nextCounts }));
  }, [quoterDataFromDetails, selectedInsurance]);

  // Hidrata selectedInsurance con el productId de la cotización cargada — corre una sola vez
  // para no pisar cambios manuales posteriores del usuario.
  useEffect(() => {
    if (productHydrated.current) return;
    if (!quoterDataFromDetails?.productId || !apiData?.data?.length) return;
    productHydrated.current = true;
    const match = apiData.data.find(p => String(p.id) === String(quoterDataFromDetails.productId));
    if (match) {
      setSelectedInsurance(match);
      // Si tiene subs, el efecto de subproductId existente resuelve `product` — no lo tocamos aquí.
    } else {
      const firstActiveProduct = apiData.data.find(p => p.active);
      if (firstActiveProduct) {
        setSelectedInsurance(firstActiveProduct);
        if (firstActiveProduct.subs?.length > 0) setProduct(firstActiveProduct.subs[0].id);
      }
    }
  }, [quoterDataFromDetails, apiData]);

  // ============ EFFECTS - Geolocation ============
  useEffect(() => {
    if (!selectedInsurance?.origins?.length || selectedOrigin || geolocating || userClearedOrigin.current) return;
    const detectOriginCountry = async () => {
      setGeolocating(true);
      try {
        const response = await fetch('https://geo-detector.long-king-001d.workers.dev/');
        if (!response.ok) return;
        const data = await response.json();
        if (data.success && data.country) {
          const detectedCountry = data.country.toUpperCase();
          let matchingOrigin = selectedInsurance.origins.find(o => o?.country?.code?.toUpperCase() === detectedCountry);
          if (!matchingOrigin) {
            const countryNames = {
              'AR': 'Argentina', 'BO': 'Bolivia', 'BR': 'Brasil', 'CA': 'Canadá',
              'CL': 'Chile', 'CO': 'Colombia', 'CR': 'Costa Rica', 'EC': 'Ecuador',
              'SV': 'El Salvador', 'ES': 'España', 'GT': 'Guatemala', 'HN': 'Honduras',
              'MX': 'México', 'PA': 'Panamá', 'PY': 'Paraguay', 'PE': 'Perú',
              'DO': 'República Dominicana', 'UY': 'Uruguay', 'VE': 'Venezuela'
            };
            const countryName = countryNames[detectedCountry];
            if (countryName) matchingOrigin = selectedInsurance.origins.find(o => o?.country?.name === countryName);
          }
          if (!matchingOrigin) {
            matchingOrigin = selectedInsurance.origins.find(o => o?.country?.name === 'Mundial');
          }
          if (matchingOrigin?.country?.id) setSelectedOrigin(matchingOrigin.country.id);
          setDetectedCountryCode(detectedCountry);
        }
      } catch (error) {
      } finally {
        setGeolocating(false);
      }
    };
    const timer = setTimeout(detectOriginCountry, 300);
    return () => clearTimeout(timer);
  }, [selectedInsurance, selectedOrigin, geolocating]);

  // ============ EFFECTS - Dial Code Sync ============
  // Prioridad: a) manual > b) país de origen > c) geolocalización (default)

  // b) Si cambia el país de origen, sincroniza el indicativo — salvo que el
  // usuario ya lo haya elegido manualmente.
  useEffect(() => {
    if (dialCodeManuallySet.current) return;
    if (!selectedOrigin || !selectedInsurance?.origins) return;
    const originItem = selectedInsurance.origins.find(o => o?.country?.id === selectedOrigin);
    const iso = originItem?.country?.code?.toUpperCase();
    if (iso && getDialEntry(iso, true)) setDialCountryCode(iso);
  }, [selectedOrigin, selectedInsurance]);

  // c) Mientras no haya un origen resuelto, usa la geolocalización como default.
  useEffect(() => {
    if (dialCodeManuallySet.current) return;
    if (selectedOrigin) return;
    if (detectedCountryCode) setDialCountryCode(detectedCountryCode.toUpperCase());
  }, [detectedCountryCode, selectedOrigin]);

  // a) Selección manual del indicativo — congela la sincronización automática.
  const handleDialCountryCodeChange = useCallback((code) => {
    dialCodeManuallySet.current = true;
    setDialCountryCode(code);
  }, []);

  // Reset origin confirmation when insurance type changes
  useEffect(() => { setOriginConfirmed(false); }, [selectedInsurance]);

  // ============ EFFECTS - Timezone MinDate ============
  useEffect(() => {
    if (!selectedOrigin || !selectedInsurance?.origins) {
      setMinDateForPicker(new Date());
      return;
    }
    try {
      const originCountry = selectedInsurance.origins.find(o => o?.country?.id === selectedOrigin);
      if (!originCountry?.country?.name) { setMinDateForPicker(new Date()); return; }
      const timezone = COUNTRY_TIMEZONES[originCountry.country.name] || 'UTC';
      const nowInOriginTimezone = toZonedTime(new Date(), timezone);
      setMinDateForPicker(nowInOriginTimezone);
    } catch (error) {
      setMinDateForPicker(new Date());
    }
  }, [selectedOrigin, selectedInsurance]);

  // Auto-set destination = origin for Viaje Nacional
  useEffect(() => {
    const national = selectedInsurance?.name?.toLowerCase().includes('nacional');
    if (national && selectedOrigin) {
      setSelectedDestinations([selectedOrigin]);
    }
  }, [selectedOrigin, selectedInsurance]);

  // Viaje internacional: el destino nunca puede ser el país de origen (solo
  // Viaje Nacional repite país). Las opciones ya lo excluyen, pero una
  // cotización precargada (p. ej. del WhatsApp Flow, México → México) o un
  // cambio de origen después de elegir destino lo dejaban colarse.
  useEffect(() => {
    const national = selectedInsurance?.name?.toLowerCase().includes('nacional');
    if (national || !selectedOrigin) return;
    if (selectedDestinations.includes(selectedOrigin)) {
      setSelectedDestinations(prev => prev.filter(d => d !== selectedOrigin));
    }
  }, [selectedOrigin, selectedInsurance, selectedDestinations]);

  // ============ API ============
  const fetchApiData = async () => {
    try {
      setLoading(true);
      setError(null);
      const productsResponse = await fetch(process.env.REACT_APP_API_BACKEND + '/external/quoter/products', {
        method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' }
      });
      if (!productsResponse.ok) throw new Error(`Error loading products: ${productsResponse.status}`);
      const productsData = await productsResponse.json();
      setApiData(productsData);

      // Fetch countries (not used directly but kept for compatibility)
      try {
        await fetch(process.env.REACT_APP_API_BACKEND + '/conf/countries', {
          method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' }
        });
      } catch (e) {}

      // Si hay un quoterId presente, no auto-seleccionamos el primer producto:
      // dejamos selectedInsurance en null y que lo resuelva la hidratación desde quoterDataFromDetails.
      if (productsData?.data?.length > 0 && !getQuoterIdSafe()) {
        const firstActiveProduct = productsData.data.find(p => p.active);
        if (firstActiveProduct) {
          setSelectedInsurance(firstActiveProduct);
          if (firstActiveProduct.subs?.length > 0) setProduct(firstActiveProduct.subs[0].id);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchApiData(); }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (destinationDebounceTimer.current) {
        clearTimeout(destinationDebounceTimer.current);
      }
      if (passengersDebounceTimer.current) {
        clearTimeout(passengersDebounceTimer.current);
      }
    };
  }, []);

  // Clear destination timer when user manually changes panel/step (desktop)
  useEffect(() => {
    if (activePanel !== 'destination' && destinationDebounceTimer.current) {
      clearTimeout(destinationDebounceTimer.current);
      destinationDebounceTimer.current = null;
    }
  }, [activePanel]);

  // Clear destination timer when user manually changes step (mobile)
  useEffect(() => {
    if (mobileActiveStep !== 'destination' && destinationDebounceTimer.current) {
      clearTimeout(destinationDebounceTimer.current);
      destinationDebounceTimer.current = null;
    }
  }, [mobileActiveStep]);

  // Clear passengers timer when user manually changes panel/step (desktop)
  useEffect(() => {
    if (activePanel !== 'passengers' && passengersDebounceTimer.current) {
      clearTimeout(passengersDebounceTimer.current);
      passengersDebounceTimer.current = null;
    }
  }, [activePanel]);

  // Clear passengers timer when user manually changes step (mobile)
  useEffect(() => {
    if (mobileActiveStep !== 'passengers' && passengersDebounceTimer.current) {
      clearTimeout(passengersDebounceTimer.current);
      passengersDebounceTimer.current = null;
    }
  }, [mobileActiveStep]);

  // Reset dates when origin country changes to match new timezone
  // Skip during hydration to preserve dates from database
  useEffect(() => {
    if (!selectedOrigin || !selectedInsurance) return;

    if (isHydrating.current) {
      isHydrating.current = false;
      return;
    }

    const originItem = selectedInsurance.origins?.find(o => o.country?.id === selectedOrigin);
    const originCountry = originItem?.country?.name || 'Mundial';
    const timezone = COUNTRY_TIMEZONES[originCountry] || 'UTC';
    const newMinDate = toZonedTime(new Date(), timezone);

    // Reset start date if it's before the new minimum date (null means not yet selected)
    if (startDate !== null && startDate < newMinDate) {
      setStartDate(newMinDate);
    }

    // Reset end date if it's before the new minimum date (null means not yet selected)
    if (endDate !== null && endDate < newMinDate) {
      setEndDate(newMinDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrigin, selectedInsurance]);

  // TODO: Auto-abrir contacto cuando cierra el panel de viajeros
  // (Temporalmente comentado - buscando solución estable)
  // useEffect(() => {
  //   if (prevActivePanel.current === 'passengers' && activePanel !== 'passengers' && activePanel !== 'contact') {
  //     if (showContact && layoutMode !== 'mobile') {
  //       if (passengersDebounceTimer.current) {
  //         clearTimeout(passengersDebounceTimer.current);
  //       }
  //       setTimeout(() => setActivePanel('contact'), 100);
  //     }
  //   }
  //   prevActivePanel.current = activePanel;
  // }, [activePanel, showContact, layoutMode]);





  // ============ VALIDATION ============
  const validateFieldsWithErrors = () => {
    const errors = { destino: false, fechas: false, viajeros: false, producto: false, email: false, phone: false };
    if (!selectedInsurance) return false;
    const hasSubproducts = selectedInsurance.subs?.length > 0;
    if (!hasSubproducts && selectedInsurance.destinations?.length > 0 && selectedDestinations.length === 0) {
      errors.destino = true;
    }
    if (!isNational && selectedOrigin && selectedDestinations.includes(selectedOrigin)) {
      errors.destino = true;
    }
    // Salida anterior a hoy (en la hora del país de origen, igual que el
    // calendario): el calendario no deja elegirla, pero así tampoco pasa una
    // fecha que llegó precargada.
    if (startDate) {
      const minimo = new Date(minDateForPicker);
      minimo.setHours(0, 0, 0, 0);
      if (startDate < minimo) errors.fechas = true;
    }
    if (selectedInsurance.dateType === 'range') {
      if (!startDate || !endDate) {
        errors.fechas = true;
      } else {
        const DAY_MS = 1000 * 60 * 60 * 24;
        const days = Math.floor((endDate - startDate) / DAY_MS) + 1;
        const minDays = isNational ? 2 : 3;
        if (days < minDays) errors.fechas = true;
      }
    } else if (selectedInsurance.dateType === 'single') {
      if (!startDate) errors.fechas = true;
    }
    if (guests === 0) errors.viajeros = true;
    if (hasSubproducts && !product) errors.producto = true;
    if (selectedInsurance.email && (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))) errors.email = true;
    if (selectedInsurance.phone) {
      // La normalización (incl. el "1" de móvil mexicano) y el uso correcto de
      // la API viven en utils/phone.js — ver el porqué allí.
      const country = dialCountryCode || detectedCountryCode || 'CO';
      if (!isPhonePossible(phone, country)) {
        errors.phone = true;
      }
    }
    setValidationErrors(errors);
    return !Object.values(errors).some(e => e === true);
  };

  // ============ SUBMIT ============
  const sendData = async (quoterData) => {
    // ⚠️ TODO ESTE BLOQUE VA ANTES DEL PRIMER await, Y NO ES ESTÉTICO.
    //
    // La activación por clic del usuario se agota en cuanto el manejador cede
    // el control. Abrir la pestaña después de esperar la respuesta del api
    // llegaba siempre tarde: el navegador la bloqueaba y el usuario acababa
    // viendo el enlace de reserva en vez de los planes. Abrirla aquí, en el
    // mismo instante del clic, y rellenarla luego con la URL real es la única
    // forma de que el navegador la acepte.
    //
    // handleSearch no hace ningún await antes de llamar aquí, así que este
    // punto sigue dentro de la activación. Si algún día se le añade uno, este
    // arreglo deja de funcionar sin dar ninguna señal.
    const locale = window?.QuoterConfig?.locale || 'es';
    const landing = process.env.REACT_APP_LANDING || 'https://segurosfly.com';

    // Resolución de newTab en tres niveles. Nunca se traga un error en
    // silencio: si no se puede determinar, se dice por consola.
    const newTab = (() => {
      // 1. Fuente explícita: quien embebe el widget la declara.
      const explicito = window?.QuoterConfig?.newTab;
      if (typeof explicito === 'boolean') return explicito;

      // 2. Rastrear la etiqueta de cotizador.js y leer su query.
      const scripts = Array.from(document.getElementsByTagName('script'));
      for (const s of scripts) {
        const src = s.getAttribute('src');
        if (src && src.includes('cotizador.js')) {
          try {
            // El src es absoluto; document.baseURI cubre el caso relativo y
            // es válido incluso en un documento srcdoc.
            return new URL(src, document.baseURI).searchParams.get('newTab') === 'true';
          } catch (e) {
            console.warn('[Quoter] No se pudo determinar newTab: el src de cotizador.js no se pudo parsear.', src, e);
            return false;
          }
        }
      }

      console.warn('[Quoter] No se pudo determinar newTab: no se encontró la etiqueta de cotizador.js.');
      return false;
    })();

    // Candado independiente de la bandera. Dentro de un iframe la navegación
    // en el mismo documento SIEMPRE falla: segurosfly.com rechaza ser embebido
    // y el usuario ve "refused to connect". No puede depender de que una
    // bandera se lea bien.
    const dentroDeIframe = window.self !== window.top;
    const abrirEnPestania = newTab || dentroDeIframe;

    // Sin 'noopener' a propósito: con esa bandera el navegador devuelve null y
    // nos quedaríamos sin la referencia que hace falta para asignar la URL
    // después. La protección se recupera anulando opener a mano más abajo.
    const ventana = abrirEnPestania ? window.open('about:blank', '_blank') : null;

    try {
      const utmParams = window?.QuoterConfig?.utmParams || null;
      // SF-145: marca del clic del anuncio (fbc/fbp/fbclid/gclid). Si no hay
      // nada, no se manda el campo.
      const marcaClic = leerMarcaClic();
      const dataWithUTMs = { ...quoterData, utmParams, ...(marcaClic ? { marcaClic } : {}) };
      const idParam = (quoterData?.id != null) ? String(quoterData.id) : effectiveQuoterId;
      const url = `${process.env.REACT_APP_API_BACKEND}/external/quoterlanding/create?id=${encodeURIComponent(idParam)}`;
      const headers = { 'Content-Type': 'application/json' };

      // Se reevalúa EN ESTE MOMENTO y no se confía solo en el estado del
      // montaje: la clave puede llegar por QuoterConfig después de que el
      // efecto inicial ya haya corrido, y una atribución perdida no se
      // recupera. Leer dos veces es gratis; cobrarle la comisión a la agencia
      // equivocada, no.
      const claveEfectiva = clientKey ?? getClientKeySafe();
      if (claveEfectiva) {
        headers['x-client-key'] = claveEfectiva;
      } else {
        // Visible a propósito. Sin esta cabecera el api tiene que adivinar por
        // Origin, y desde un dominio compartido por varias agencias adivinar es
        // siempre equivocarse.
        console.warn(
          '[cotizador] No se pudo determinar la clave del cliente: la cotización ' +
            'se atribuirá por Origin. Si esto pasa en el portal de agencias, es un fallo.',
        );
      }
      const response = await fetch(url, {
        method: 'POST', credentials: 'include',
        headers,
        body: JSON.stringify(dataWithUTMs)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear la cotización');
      }
      const result = await response.json();
      // El api responde 201 con success:false cuando rechaza por una regla de
      // negocio (fecha pasada, origen = destino…). Antes esto reventaba en
      // `result.data.id` y el usuario veía un error técnico.
      if (!result?.success || !result?.data?.id) {
        throw new Error(result?.message || 'No se pudo crear la cotización');
      }
      const uuidquoter = result.data.id;

      // Push quote event to GTM dataLayer (available in parent page)
      try {
        const totalGuests = Object.values(passengerCounts).reduce((sum, c) => sum + c, 0);
        const totalDays = (startDate && endDate)
          ? Math.floor((endDate.getTime() - startDate.getTime()) / 86400000) + 1
          : 0;
        const destNames = selectedDestinations.map(id => {
          const opt = destinationOptions.find(o => o.value === id);
          return opt?.data?.name;
        }).filter(Boolean);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'quote',
          quote_type: 'seguro_viaje',
          quote_location: layoutMode === 'mobile' ? 'mobile_cotizador' : 'desktop_cotizador',
          quote_data: {
            timestamp: new Date().toISOString(),
            product_type: String(selectedInsurance?.id || ''),
            product_name: selectedInsurance?.name || '',
            origin: getOriginSummary(),
            destinations: destNames,
            start_date: quoterData.initial_date || '',
            end_date: quoterData.end_date || '',
            travelers_total: totalGuests,
            total_days: totalDays,
          },
          quoter_id: String(uuidquoter),
        });
      } catch (_) {}

      const urlPlanes = `${landing}/${locale}/cotizar/plans/${uuidquoter}`;

      // Aviso a quien embeba el widget: acaba de nacer una cotización. El
      // Portal Partners lo escucha para refrescar su tabla sin recargar la
      // página; desde fuera del iframe el documento no tiene forma de
      // enterarse de lo que pasa aquí dentro.
      //
      // Inocuo en cualquier otro contexto: en segurosfly.com y en las landings
      // window.parent es esta misma ventana y nadie escucha este tipo.
      try {
        window.parent?.postMessage(
          { tipo: 'sf-cotizacion-creada', quoterId: uuidquoter },
          '*'
        );
      } catch (e) {
        console.warn('[Quoter] No se pudo avisar de la cotización creada.', e);
      }

      if (abrirEnPestania) {
        if (ventana) {
          ventana.location.href = urlPlanes;
          // Se recupera aquí la protección que no se pudo pedir con 'noopener'.
          // Va DESPUÉS de asignar la URL: en este instante la pestaña sigue en
          // about:blank y es del mismo origen, así que aún se deja tocar.
          try { ventana.opener = null; } catch (e) {}
        } else {
          // Bloqueada incluso abriéndola en el clic. NO se navega este
          // documento: dentro de un iframe dejaría al usuario con la pantalla
          // de error. Se le ofrece un enlace para que la abra él.
          console.warn('[Quoter] El navegador bloqueó la ventana emergente; se ofrece un enlace para abrir los planes.');
          setUrlPlanesBloqueada(urlPlanes);
        }
      } else {
        window.location.href = urlPlanes;
      }
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      // La cotización no llegó a existir: la pestaña en blanco que se abrió en
      // el clic se queda huérfana y hay que cerrarla, o el usuario se encuentra
      // un about:blank sin explicación.
      if (ventana) {
        try { ventana.close(); } catch (e) {}
      }
      alert('Error al crear la cotización: ' + error.message);
    }
  };

  // Helper function to format date without UTC conversion
  const formatLocalDate = (date) => {
    if (!date || typeof date.getTime !== 'function' || isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSearch = async () => {
    if (!validateFieldsWithErrors()) return false;

    try {
      // Red final: en un producto anual la llegada se RECALCULA siempre, no
      // solo cuando falta. Comprobar únicamente que estuviera vacía era lo que
      // dejaba pasar el caso real: al cambiar de Viajes Puntuales a Múltiples
      // Viajes, endDate no estaba vacía — traía el 29/09 del viaje puntual.
      let effectiveEndDate = endDate;
      if (esProductoAnual(selectedInsurance?.dateType) && startDate) {
        effectiveEndDate = finDeMultiplesViajes(startDate);
      }

      const agesData = [];
      if (selectedInsurance.passengers?.length > 0) {
        selectedInsurance.passengers.forEach(passenger => {
          const count = passengerCounts[passenger.id] || 0;
          if (count > 0) {
            agesData.push({ id: passenger.age_id, name: passenger.name, count });
          }
        });
      }
      // E.164 ya normalizado: sin el "1" mexicano, para que el mismo número no
      // genere duplicados en Kommo ni falle el envío por WhatsApp.
      const normalizedPhone = toPhoneE164(phone, dialCountryCode || detectedCountryCode || 'CO');

      const quoterData = {
        email: email || '', phone: normalizedPhone,
        marketing_consent: true,
        initial_date: formatLocalDate(startDate),
        end_date: formatLocalDate(effectiveEndDate),
        countryOriginId: selectedOrigin?.id || selectedOrigin,
        country_destinations: selectedDestinations.map(dest => dest?.id || dest),
        passengers: agesData, passengers_ages: agesData.length,
        productId: selectedInsurance.id,
        subproductId: (product && typeof product === 'number' && product > 0) ? product : null,
        obs: '',
      };
      sendData(quoterData);
    } catch (error) {
      alert('Error al crear la cotización: ' + error.message);
    }
  };

  // ============ MEMOIZED OPTIONS ============
  const buildCountryOptions = (countries) => {
    if (!countries) return [];
    return countries.map(item => {
      if (!item.country) return null;
      const country = item.country;
      return {
        value: country.id,
        data: country,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {country.flag && (
              typeof country.flag === 'string' && (country.flag.startsWith('http') || country.flag.startsWith('/') || country.flag.endsWith('.svg') || country.flag.endsWith('.png') || country.flag.endsWith('.jpg') || country.flag.endsWith('.webp'))
                ? <img src={country.flag.startsWith('http') ? country.flag : `${process.env.REACT_APP_API_BACKEND}${country.flag.startsWith('/') ? '' : '/'}${country.flag}`} alt="" loading="lazy" decoding="async" style={{ width: '18px', height: '12px', objectFit: 'cover' }} />
                : <span style={{ fontSize: '16px' }}>{country.flag}</span>
            )}
            <span>{country.name}</span>
          </div>
        )
      };
    }).filter(Boolean);
  };

  const originOptions = useMemo(() => buildCountryOptions(selectedInsurance?.origins), [selectedInsurance]);
  const destinationOptions = useMemo(() => {
    const national = selectedInsurance?.name?.toLowerCase().includes('nacional');
    if (national) {
      // For national, destination options come from origins (same country)
      return buildCountryOptions(selectedInsurance?.origins);
    }
    const allOptions = buildCountryOptions(selectedInsurance?.destinations);
    // Filter out the selected origin to prevent selecting the same country
    return allOptions.filter(option => option.value !== selectedOrigin);
  }, [selectedInsurance, selectedOrigin]);

  // ============ SUMMARIES ============
  const getOriginSummary = () => {
    if (!selectedOrigin) return '';
    const opt = originOptions.find(o => o.value === selectedOrigin);
    return opt?.data?.name || '';
  };

  const getDestinationSummary = () => {
    if (selectedDestinations.length === 0) return '';
    const names = selectedDestinations.map(id => {
      const opt = destinationOptions.find(o => o.value === id);
      return opt?.data?.name;
    }).filter(Boolean);
    return names.join(', ');
  };

  const getProductSummary = () => {
    if (!product || !selectedInsurance?.subs) return '';
    const sub = selectedInsurance.subs.find(s => s.id === product);
    return sub?.name || '';
  };

  const getDateSummary = () => {
    if (!startDate) return '';
    const opts = { day: '2-digit', month: 'short', year: '2-digit' };
    if (selectedInsurance?.dateType === 'range' && endDate) {
      return `${startDate.toLocaleDateString('es', opts)} - ${endDate.toLocaleDateString('es', opts)}`;
    }
    if (esProductoAnual(selectedInsurance?.dateType)) {
      // El resumen calcula la llegada por su cuenta en vez de leer endDate: es
      // lo que el usuario VE, y tiene que decir el año aunque el estado esté a
      // medio reconciliar en el render en que se cambia de producto.
      const calculatedEndDate = finDeMultiplesViajes(startDate);
      return `${startDate.toLocaleDateString('es', opts)} - ${calculatedEndDate.toLocaleDateString('es', opts)}`;
    }
    return startDate.toLocaleDateString('es', opts);
  };

  const getDurationSummary = () => {
    if (!startDate || !endDate) return '';
    const DAY_MS = 1000 * 60 * 60 * 24;
    const days = Math.floor((endDate - startDate) / DAY_MS) + 1;
    return days > 0 ? `${days} día${days !== 1 ? 's' : ''} de viaje` : '';
  };

  const getPassengersSummary = () => {
    if (guests === 0) return '';
    return `${guests} ${guests === 1 ? 'viajero' : 'viajeros'}`;
  };

  // ============ CONDITIONAL VISIBILITY ============
  const isNational = selectedInsurance?.name?.toLowerCase().includes('nacional');
  selectedInsuranceRef.current = selectedInsurance;

  const hasActiveProducts = apiData?.data?.length > 0 && apiData.data.some(p => p.active);
  const showOrigin = selectedInsurance?.origins?.length > 0;
  const showDestination = selectedInsurance?.destinations?.length > 0 && (!selectedInsurance?.subs || selectedInsurance.subs.length === 0);
  const showProduct = selectedInsurance?.subs?.length > 0;
  const showDates = selectedInsurance && selectedInsurance.dateType && selectedInsurance.dateType !== 'NA';
  const showContact = selectedInsurance && (selectedInsurance.email || selectedInsurance.phone);
  const showEmail = selectedInsurance && selectedInsurance.email;
  const showPhone = selectedInsurance && selectedInsurance.phone;

  // ============ HANDLER CALLBACKS ============
  const handlePassengerCountChange = useCallback((passengerId, newCount) => {
    setPassengerCounts(prev => {
      const updated = { ...prev, [passengerId]: newCount };

      // Check if we have at least one passenger
      const totalPassengers = Object.values(updated).reduce((sum, count) => sum + count, 0);

      if (totalPassengers > 0) {
        setValidationErrors(prev => ({ ...prev, viajeros: false }));
      }

      return updated;
    });

    // Clear any existing timer
    if (passengersDebounceTimer.current) {
      clearTimeout(passengersDebounceTimer.current);
    }

    // Auto-advance desactivado
  }, []);

  const handleDateRangeChange = useCallback((dates) => {
    const [start, end] = dates;
    setStartDate(start);
    if (start && end) {
      const DAY_MS = 1000 * 60 * 60 * 24;
      const days = Math.floor((end - start) / DAY_MS) + 1;
      const national = selectedInsuranceRef.current?.name?.toLowerCase().includes('nacional');
      const minDays = national ? 2 : 3;
      if (days < minDays) {
        const adjustedEnd = new Date(start);
        adjustedEnd.setDate(adjustedEnd.getDate() + minDays - 1);
        setEndDate(adjustedEnd);
        setMinDaysAdjusted(true);
      } else {
        setEndDate(end);
        setMinDaysAdjusted(false);
      }
      setValidationErrors(prev => ({ ...prev, fechas: false }));
      if (layoutMode === 'mobile') {
        setTimeout(() => setMobileActiveStep('passengers'), 300);
      }
    } else {
      setEndDate(end);
      setMinDaysAdjusted(false);
    }
  }, [layoutMode]);

  const handleSingleDateChange = useCallback((date) => {
    setStartDate(date);
    if (date && esProductoAnual(selectedInsurance?.dateType)) {
      setEndDate(finDeMultiplesViajes(date));
    }
    if (date) {
      setValidationErrors(prev => ({ ...prev, fechas: false }));

      // Auto-advance desactivado
    }
  }, [selectedInsurance?.dateType]);

  /**
   * La invariante del producto anual, mantenida pase lo que pase.
   *
   * ESTE es el arreglo del bug. El recálculo de la llegada solo ocurría al
   * tocar el calendario, así que cambiar el producto de Viajes Puntuales a
   * Múltiples Viajes dejaba intactas las fechas del viaje puntual: producto
   * anual con rango de 29 días, y precio calculado sobre ese tramo.
   *
   * El reseteo por cambio de producto YA EXISTÍA —«EFFECTS - Insurance Reset»,
   * más arriba: limpia origen, destinos, subproducto y viajeros—. Simplemente
   * se olvidó las fechas, y las fechas son el único de esos campos que sigue
   * teniendo sentido aparente después del cambio, así que nada se veía roto.
   *
   * Va en un efecto aparte y no dentro de aquel porque no es lo mismo: aquel
   * BORRA al cambiar de producto, y este mantiene una invariante que también
   * tiene que cumplirse cuando lo que cambia es la salida. Además cubre las
   * cuatro vías por las que se llama a setSelectedInsurance —escritorio, móvil,
   * el overlay y la precarga—, y un arreglo repetido cuatro veces es un arreglo
   * que a la quinta llamada no está.
   *
   * Se compara por día antes de escribir: sin eso, cada render crearía un Date
   * nuevo, distinto por identidad del anterior, y el efecto se dispararía a sí
   * mismo sin parar.
   */
  const tipoFechaAnterior = useRef(null);
  useEffect(() => {
    const tipo = selectedInsurance?.dateType ?? null;
    const anterior = tipoFechaAnterior.current;
    tipoFechaAnterior.current = tipo;

    if (esProductoAnual(tipo)) {
      const esperado = finDeMultiplesViajes(startDate);
      setEndDate((actual) => (mismoDia(actual, esperado) ? actual : esperado));
      return;
    }

    // Se SALE de un producto anual: se libera la llegada. Heredarla convertiría
    // el siguiente producto por tramos en un viaje de 365 días sin que nadie lo
    // haya pedido — el mismo error, en el sentido contrario.
    if (esProductoAnual(anterior)) {
      setEndDate(null);
    }
  }, [selectedInsurance?.dateType, startDate]);

  const handleDestinationInputChange = useCallback(() => {
    // Cuando el usuario está escribiendo, cancelar el timer de auto-avance
    if (destinationDebounceTimer.current) {
      clearTimeout(destinationDebounceTimer.current);
      destinationDebounceTimer.current = null;
    }
  }, []);

  const handleDestinationChange = useCallback((dests) => {
    setSelectedDestinations(dests);
    if (dests.length > 0) {
      setValidationErrors(prev => ({ ...prev, destino: false }));

      // Cancelar timer anterior si existe
      if (destinationDebounceTimer.current) {
        clearTimeout(destinationDebounceTimer.current);
      }

      // Auto-advance desactivado
    }
  }, []);

  const handleProductChange = useCallback((val) => {
    setProduct(val);
    if (val) {
      setValidationErrors(prev => ({ ...prev, producto: false }));

      // Auto-advance desactivado
    }
  }, []);

  const handleEmailChange = useCallback((val) => {
    setEmail(val);
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
    setValidationErrors(prev => ({ ...prev, email: val.trim() ? !isValid : prev.email }));
  }, []);

  const handlePhoneChange = useCallback((val) => {
    if (val && !val.startsWith('+')) {
      setPhone('+' + val);
    } else {
      setPhone(val);
    }
  }, []);

  // ============ RENDER FIELD FUNCTIONS (shared between desktop & mobile) ============
  const renderInsuranceContent = () => (
    <InsuranceTypeField
      products={apiData?.data || []}
      selectedInsurance={selectedInsurance}
      onSelect={(ins) => {
        if (layoutMode === 'mobile') {
          // flushSync: fuerza render sincrónico para que el input del siguiente paso
          // exista en el DOM antes de llamar focus(), dentro del contexto del gesto
          const nextStep = showOrigin ? 'origin' : showDestination ? 'destination' : 'dates';
          const isKeyboard = nextStep === 'origin' || nextStep === 'destination';
          flushSync(() => {
            setSelectedInsurance(ins);
            setMobileActiveStep(nextStep);
          });
          if (isKeyboard) {
            const input = document.querySelector('.sf-mobile-sheet__fullscreen-content .react-select__input input');
            if (input) input.focus();
          }
        } else {
          setSelectedInsurance(ins);
          setTimeout(() => {
            if (showOrigin) setActivePanel('origin');
            else if (showDestination) setActivePanel('destination');
            else if (showProduct) setActivePanel('product');
            else setActivePanel('dates');
          }, 200);
        }
      }}
    />
  );

  const renderOriginContent = () => (
    <OriginField
      options={originOptions}
      value={selectedOrigin}
      onChange={(val) => {
        if (!val) {
          userClearedOrigin.current = true;
        } else {
          userClearedOrigin.current = false;
        }
        setSelectedOrigin(val);
        // Remove the new origin from destinations if it's already selected there
        if (val && selectedDestinations.includes(val)) {
          setSelectedDestinations(prev => prev.filter(destId => destId !== val));
        }
            // Auto-advance desactivado
      }}
      customFilterOption={customFilterOption}
      onMenuOpen={() => { setIsDatePickerOpen(false); }}
      isOpen={layoutMode === 'mobile' ? mobileActiveStep === 'origin' : activePanel === 'origin'}
    />
  );

  const renderDestinationContent = () => (
    <DestinationField
      options={destinationOptions}
      value={selectedDestinations}
      onChange={handleDestinationChange}
      onInputChange={handleDestinationInputChange}
      customFilterOption={customFilterOption}
      validationError={validationErrors.destino}
      onMenuOpen={() => { setIsDatePickerOpen(false); }}
      isOpen={layoutMode === 'mobile' ? mobileActiveStep === 'destination' : activePanel === 'destination'}
      isDisabled={isNational}
    />
  );

  const renderProductContent = () => (
    <ProductField
      subs={selectedInsurance?.subs || []}
      value={product}
      onChange={handleProductChange}
      validationError={validationErrors.producto}
    />
  );

  const renderDatesContent = () => (
    <>
      {minDaysAdjusted && (
        <div className="sf-min-days-msg">
          {isNational
            ? 'Las coberturas aplican desde 2 días de viaje'
            : 'Las coberturas aplican desde 3 días de viaje'}
        </div>
      )}
      <DateField
      key={`datepicker-${selectedOrigin}`}
      dateType={selectedInsurance?.dateType}
      startDate={startDate}
      endDate={endDate}
      onChangeRange={handleDateRangeChange}
      onChangeSingle={handleSingleDateChange}
      minDate={minDateForPicker}
      maxDays={isNational ? 90 : 365}
      isOpen={isDatePickerOpen}
      onOpen={() => { setIsDatePickerOpen(true); }}
      onClose={() => setIsDatePickerOpen(false)}
      validationError={validationErrors.fechas}
      inline={layoutMode === 'mobile' || activePanel === 'dates'}
      monthsShown={layoutMode === 'mobile' ? 1 : 2}
      onLoadMoreMonths={null}
      isMobile={layoutMode === 'mobile'}
      hidePresets={layoutMode === 'mobile'}
    />
    </>
  );

  const renderPassengersContent = () => (
    <PassengersField
      passengers={selectedInsurance?.passengers || []}
      counts={passengerCounts}
      onCountChange={handlePassengerCountChange}
      validationError={validationErrors.viajeros}
    />
  );

  // renderContactContent removed - email field is always visible in desktop searchbar

  // ============ RENDER ============
  return (
    <div className="sf-quoter" style={{ width: '100%' }}>
      {loading && (
        <div className="sf-loading">Cargando datos...</div>
      )}

      {error && (
        <div className="sf-error">Error: {error}</div>
      )}

      {!loading && !hasActiveProducts && (
        <div className="sf-no-products">
          No hay productos disponibles. Contáctanos para asistencia.
        </div>
      )}

      {!loading && hasActiveProducts && (
        <>
          {/* DESKTOP / TABLET */}
          {(layoutMode === 'desktop' || layoutMode === 'tablet') && (
            <SearchBar
              selectedInsurance={selectedInsurance}
              products={apiData?.data || []}
              showOrigin={showOrigin}
              originSummary={getOriginSummary()}
              showDestination={showDestination}
              destinationSummary={getDestinationSummary()}
              showProduct={showProduct}
              productSummary={getProductSummary()}
              showDates={showDates}
              dateSummary={getDateSummary()}
              passengersSummary={getPassengersSummary()}
              showContact={showContact}
              showEmail={showEmail}
              showPhone={showPhone}
              email={email}
              onEmailChange={handleEmailChange}
              emailError={validationErrors.email}
              phone={phone}
              onPhoneChange={handlePhoneChange}
              phoneError={validationErrors.phone}
              dialCountryCode={dialCountryCode}
              onDialCountryCodeChange={handleDialCountryCodeChange}
              destinoError={validationErrors.destino}
              viajerosError={validationErrors.viajeros}
              fechasError={validationErrors.fechas}
              productoError={validationErrors.producto}
              activePanel={activePanel}
              onPanelChange={setActivePanel}
              onSearch={handleSearch}
              renderInsurancePanel={renderInsuranceContent}
              renderOriginPanel={renderOriginContent}
              renderDestinationPanel={renderDestinationContent}
              renderProductPanel={renderProductContent}
              renderDatesPanel={renderDatesContent}
              renderPassengersPanel={renderPassengersContent}
            />
          )}

          {/* MOBILE */}
          {layoutMode === 'mobile' && (
            <>
              {/* Trigger bar */}
              <div className="sf-mobile-trigger" onClick={() => {
                try {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: 'cta_click',
                    cta_name: 'Abrir Cotizador',
                    cta_location: 'mobile_trigger_cotizador',
                    cta_type: 'primary',
                    page_section: 'cotizador_widget',
                  });
                } catch (_) {}
                setIsMobileFlowOpen(true);
                setMobileActiveStep('insurance');
              }}>
                <div className="sf-mobile-trigger__content">
                  <span className="sf-mobile-trigger__title">Cotiza tu Seguro</span>
                </div>
                <div className="sf-mobile-trigger__actions">
                  <button type="button" className="sf-mobile-trigger__search">
                    <FaCalculator />
                  </button>
                </div>
              </div>

              <MobileOverlay
                isOpen={isMobileFlowOpen}
                onClose={() => setIsMobileFlowOpen(false)}
                activeStep={mobileActiveStep}
                onStepChange={setMobileActiveStep}
                onSearch={async () => { const ok = await handleSearch(); if (ok !== false) setIsMobileFlowOpen(false); }}
                selectedInsurance={selectedInsurance}
                onSelectInsurance={setSelectedInsurance}
                products={apiData?.data || []}
                insuranceSummary={selectedInsurance?.name?.replace('Multiples', 'Múltiples') || ''}
                renderInsurance={renderInsuranceContent}
                showOrigin={showOrigin}
                originSummary={getOriginSummary()}
                originName={getOriginSummary()}
                originOptions={originOptions}
                selectedOrigin={selectedOrigin}
                onOriginChange={(val) => {
                  userClearedOrigin.current = !val;
                  setSelectedOrigin(val);
                  if (val && selectedDestinations.includes(val)) {
                    setSelectedDestinations(prev => prev.filter(id => id !== val));
                  }
                }}
                originConfirmed={originConfirmed}
                onOriginConfirm={() => setOriginConfirmed(true)}
                showDestination={showDestination}
                destinationSummary={getDestinationSummary()}
                destinationOptions={destinationOptions}
                selectedDestinations={selectedDestinations}
                onDestinationChange={handleDestinationChange}
                customFilterOption={customFilterOption}
                showProduct={showProduct}
                productSummary={getProductSummary()}
                renderProduct={renderProductContent}
                showDates={showDates}
                dateSummary={getDateSummary()}
                durationSummary={getDurationSummary()}
                renderDates={renderDatesContent}
                passengersSummary={getPassengersSummary()}
                renderPassengers={renderPassengersContent}
                showEmail={showEmail}
                showPhone={showPhone}
                email={email}
                onEmailChange={handleEmailChange}
                emailError={validationErrors.email}
                phone={phone}
                onPhoneChange={handlePhoneChange}
                phoneError={validationErrors.phone}
                detectedCountryCode={detectedCountryCode}
                dialCountryCode={dialCountryCode}
                onDialCountryCodeChange={handleDialCountryCodeChange}
              />
            </>
          )}

          {/* Success Message */}
          {showSuccessMessage && (
            <div className="sf-success-message">¡Cotización enviada con éxito!</div>
          )}

          {/* El navegador bloqueó la ventana emergente. Se ofrece el enlace para
              que el propio clic del usuario la abra: no se navega este documento
              porque dentro de un iframe acabaría en "refused to connect". */}
          {urlPlanesBloqueada && (
            <div className="sf-success-message">
              Tu cotización está lista.{' '}
              <a
                href={urlPlanesBloqueada}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#123550', fontWeight: 700, textDecoration: 'underline' }}
              >
                Ver planes
              </a>
            </div>
          )}
        </>
      )}


    </div>
  );
};

export default Quoter;
